import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  DndContext,
  PointerSensor,
  useDraggable,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent,
} from "@dnd-kit/core";
import { CSS } from "@dnd-kit/utilities";
import type { Service } from "@/lib/types";
import { ServiceTile } from "@/components/ServiceTile";
import {
  FREEFORM_GAP,
  FREEFORM_TILE_H_GUESS,
  FREEFORM_TILE_W,
  findFreeformSlot,
  resolveFreeformCollisions,
  type FreeformPos,
  type Rect,
} from "@/lib/dashboardLayout";

// Freeform canvas: tiles are absolutely positioned and dragged to any pixel
// spot, so the dashboard can mirror where logger boxes physically sit on site.
// Overlap is not allowed — a drop that would intersect another tile snaps the
// tile back to where it started.

function FreeformTile({
  svc,
  pos,
  active,
  registerEl,
  onClick,
}: {
  svc: Service;
  pos: FreeformPos;
  active: boolean;
  registerEl: (id: string, el: HTMLElement | null) => void;
  onClick: () => void;
}) {
  const { attributes, listeners, setNodeRef, transform, isDragging } =
    useDraggable({ id: svc.system_id });

  const setRefs = useCallback(
    (el: HTMLElement | null) => {
      setNodeRef(el);
      registerEl(svc.system_id, el);
    },
    [setNodeRef, registerEl, svc.system_id],
  );

  return (
    <div
      ref={setRefs}
      {...attributes}
      {...listeners}
      style={{
        position: "absolute",
        left: pos.x,
        top: pos.y,
        width: FREEFORM_TILE_W,
        transform: CSS.Translate.toString(transform),
        zIndex: active || isDragging ? 30 : 1,
        opacity: isDragging ? 0.85 : 1,
        cursor: isDragging ? "grabbing" : undefined,
      }}
    >
      <ServiceTile staticService={svc} onClick={onClick} />
    </div>
  );
}

export function DashboardFreeformLayout({
  services,
  positions,
  onPositionsChange,
  onOpen,
}: {
  services: Service[];
  positions: Record<string, FreeformPos>;
  onPositionsChange: (next: Record<string, FreeformPos>) => void;
  onOpen: (systemId: string) => void;
}) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
  );

  const canvasRef = useRef<HTMLDivElement>(null);
  const tileEls = useRef<Map<string, HTMLElement>>(new Map());
  const [canvasWidth, setCanvasWidth] = useState(0);
  const [activeId, setActiveId] = useState<string | null>(null);

  const registerEl = useCallback((id: string, el: HTMLElement | null) => {
    if (el) tileEls.current.set(id, el);
    else tileEls.current.delete(id);
  }, []);

  // Track canvas width so auto-placement + clamping use real bounds.
  useEffect(() => {
    const el = canvasRef.current;
    if (!el) return;
    const update = () => setCanvasWidth(el.clientWidth);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const ids = useMemo(() => services.map((s) => s.system_id), [services]);

  const heightOf = useCallback(
    (id: string) => tileEls.current.get(id)?.offsetHeight || FREEFORM_TILE_H_GUESS,
    [],
  );

  // Auto-place any service that has no saved position yet (newly joined
  // meters, or first render of this event). Runs until everything is placed.
  useEffect(() => {
    if (canvasWidth === 0) return;
    const unplaced = ids.filter((id) => !positions[id]);
    if (unplaced.length === 0) return;
    const occupied: Rect[] = ids
      .filter((id) => positions[id])
      .map((id) => ({
        x: positions[id].x,
        y: positions[id].y,
        w: FREEFORM_TILE_W,
        h: heightOf(id),
      }));
    const additions: Record<string, FreeformPos> = {};
    for (const id of unplaced) {
      const slot = findFreeformSlot(occupied, canvasWidth, heightOf(id));
      additions[id] = slot;
      occupied.push({ x: slot.x, y: slot.y, w: FREEFORM_TILE_W, h: heightOf(id) });
    }
    onPositionsChange({ ...positions, ...additions });
  }, [ids, positions, canvasWidth, heightOf, onPositionsChange]);

  function onDragEnd(e: DragEndEvent) {
    setActiveId(null);
    const id = String(e.active.id);
    const start = positions[id];
    if (!start) return;

    // The dragged tile lands exactly where dropped (x clamped to the canvas).
    const maxX = Math.max(0, canvasWidth - FREEFORM_TILE_W);
    const nextX = Math.min(maxX, Math.max(0, start.x + e.delta.x));
    const nextY = Math.max(0, start.y + e.delta.y);

    // Then push any tiles it overlaps out of the way (cascading) so the drop
    // always succeeds — no snap-back.
    const heights: Record<string, number> = {};
    for (const sid of ids) heights[sid] = heightOf(sid);
    const proposed = { ...positions, [id]: { x: nextX, y: nextY } };
    const resolved = resolveFreeformCollisions(proposed, heights, id);
    for (const sid of Object.keys(resolved)) {
      resolved[sid] = {
        x: Math.min(maxX, Math.max(0, resolved[sid].x)),
        y: resolved[sid].y,
      };
    }
    onPositionsChange(resolved);
  }

  // Canvas height = lowest tile bottom + gutter (min one tile tall).
  const canvasHeight = useMemo(() => {
    let max = FREEFORM_TILE_H_GUESS;
    for (const id of ids) {
      const p = positions[id];
      if (p) max = Math.max(max, p.y + heightOf(id));
    }
    return max + FREEFORM_GAP;
  }, [ids, positions, heightOf]);

  return (
    <DndContext
      sensors={sensors}
      onDragStart={(e: DragStartEvent) => setActiveId(String(e.active.id))}
      onDragEnd={onDragEnd}
      onDragCancel={() => setActiveId(null)}
    >
      <div
        ref={canvasRef}
        className="relative rounded-lg border border-dashed border-border"
        style={{ height: canvasHeight }}
      >
        {services.map((svc) => {
          const pos = positions[svc.system_id];
          if (!pos) return null; // placed on the next tick by the effect above
          return (
            <FreeformTile
              key={svc.system_id}
              svc={svc}
              pos={pos}
              active={activeId === svc.system_id}
              registerEl={registerEl}
              onClick={() => onOpen(svc.system_id)}
            />
          );
        })}
      </div>
    </DndContext>
  );
}
